import { Fragment } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FOCUSRING } from '../focus';
import { formatGetal, isObject, isTelling } from '../format';
import { WERKSOORT_LABEL, type Werksoort } from '../hooks/useGekozenWerksoort';
import { useSignaleringen } from '../hooks/useOverzicht';
import { WERKKWALITEIT_MEDEWERKERS, WERKKWALITEIT_NAKIJKERS } from '../tabs';
import { weektoestand } from '../toestand';
import { weeknummer } from '../week';
import { Doelbalk } from './Doelbalk';
import { Richtingstekst } from './Richtingstekst';
import type { Medewerkersoverzicht, Medewerkersrij, OverzichtWerksoort, TeamWerksoort, Weekcijfer, WerksoortSleutel } from '../work-quality.api';

/** Adres → antwoord: de werksoort in het adres heet zoals de lezer haar kent, het antwoord gebruikt de dienstsleutel. */
export const API_SLEUTEL: Record<Werksoort, WerksoortSleutel> = { vastleggen: 'myxxtract', controles: 'pca' };
export const ANDERE: Record<Werksoort, Werksoort> = { vastleggen: 'controles', controles: 'vastleggen' };

/** De tekens zijn `aria-hidden`; het woord ernaast draagt de betekenis. */
const TEKEN_BEKIJK = '›';
const TEKEN_NAAR = '→';

const GROEPEN = ['aandacht', 'op_koers', 'geen_cijfer'] as const;
type Groep = (typeof GROEPEN)[number];

type Cijfer = Extract<Weekcijfer, { percentage: number }>;

function isCijfer(waarde: unknown): waarde is Cijfer {
  if (!isObject(waarde)) return false;
  return waarde.reden === undefined && isTelling(waarde.percentage) && isTelling(waarde.aantalInEenKeerGoed) && isTelling(waarde.aantalBeoordeeld);
}

/** Een geldig verloop: vier weken, de laatste in de week van het cijfer. Anders "niet te laden" (matrix Mini-verloop). */
function geldigVerloop(werksoort: OverzichtWerksoort): Weekcijfer[] | null {
  const verloop = werksoort.verloop;
  if (!Array.isArray(verloop) || verloop.length !== 4) return null;
  if (!verloop.every((week) => isObject(week) && typeof week.weekKey === 'string')) return null;
  return verloop[3]!.weekKey === werksoort.cijfer?.weekKey ? verloop : null;
}

/** De werksoort van een rij, of `undefined` als het antwoord er geen object voor stuurt (ook bij JSON `null`). */
export function soortVan(rij: Medewerkersrij, sleutel: WerksoortSleutel): OverzichtWerksoort | undefined {
  const soort: unknown = isObject(rij.werksoorten) ? rij.werksoorten[sleutel] : undefined;
  return isObject(soort) ? (soort as unknown as OverzichtWerksoort) : undefined;
}

/** De groep van de dienst; een rij zonder cijfer of met een onbekende groep valt in Geen cijfer. */
function groepVan(werksoort: OverzichtWerksoort | undefined): Groep {
  if (!isObject(werksoort) || !isCijfer(werksoort.cijfer)) return 'geen_cijfer';
  return werksoort.groep === 'aandacht' || werksoort.groep === 'op_koers' ? werksoort.groep : 'geen_cijfer';
}

function useGetal() {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  return { locale, getal: (waarde: number) => formatGetal(waarde, locale) };
}

// ── Teamzin ─────────────────────────────────────────────────────────────────────────────────────

function Teamzin({ team, naam, week, doel }: { team: TeamWerksoort; naam: string; week: string; doel?: number }) {
  const { t } = useTranslation();
  const { getal } = useGetal();

  if (team.reden === 'niemand_met_cijfer') {
    return (
      <div data-testid="werkkwaliteit-teamzin">
        <p className={TEAMZIN}>{t('qa.team.teamzinNiemandMetCijfer', { week: weeknummer(week), werksoort: naam })}</p>
      </div>
    );
  }
  if (team.reden !== undefined || !isCijfer(team.cijfer)) return null;

  const cijfer = team.cijfer;
  const groepen = team.aantalPerGroep;
  const zin1 =
    isObject(groepen) && isTelling(groepen.aandacht) && isTelling(groepen.op_koers)
      ? groepen.aandacht === 0
        ? t('qa.team.teamzinNiemandAandacht', { werksoort: naam })
        : t('qa.team.teamzinAandacht', {
            count: groepen.aandacht,
            aantal: getal(groepen.aandacht),
            totaal: getal(groepen.aandacht + groepen.op_koers),
            werksoort: naam,
          })
      : null;

  const basis = { week: weeknummer(week), percentage: getal(cijfer.percentage), goed: getal(cijfer.aantalInEenKeerGoed), beoordeeld: getal(cijfer.aantalBeoordeeld) };
  const vorigeWeek = weeknummer(team.vorigeWeek?.weekKey ?? '');
  let zin2: string;
  if ((team.richting === 'hoger' || team.richting === 'lager') && isTelling(team.verschilPunten)) {
    zin2 = t(team.richting === 'hoger' ? 'qa.team.teamzinHoger' : 'qa.team.teamzinLager', {
      ...basis,
      vorigeWeek,
      count: team.verschilPunten,
      punten: getal(team.verschilPunten),
    });
  } else if (team.richting === 'gelijk') {
    zin2 = t('qa.team.teamzinGelijk', { ...basis, vorigeWeek });
  } else {
    zin2 = t('qa.team.teamzinGeenVergelijking', basis);
  }
  const vorige = isCijfer(team.vorigeWeek) ? { weekKey: team.vorigeWeek.weekKey, percentage: team.vorigeWeek.percentage } : undefined;

  return (
    <div className="flex flex-col gap-1" data-testid="werkkwaliteit-teamzin">
      <p className={TEAMZIN}>
        {zin1 !== null && <>{zin1} </>}
        {zin2}
      </p>{' '}
      <p className="text-[0.8125rem] font-medium tabular-nums text-[var(--foreground)]">
        {t('qa.team.teamCijfer', { werksoort: naam, percentage: basis.percentage, goed: basis.goed, beoordeeld: basis.beoordeeld })}
      </p>
      <Doelbalk doel={doel} percentage={cijfer.percentage} vorige={vorige} />
    </div>
  );
}

const TEAMZIN = 'text-[0.9375rem] font-semibold leading-snug text-[var(--foreground-heading)]';

// ── Nakijkers-seintje ───────────────────────────────────────────────────────────────────────────

/**
 * Het seintje naar Nakijkers: een eigen antwoord, alleen in de gewone weergave. Het telt alleen de velden van
 * dezelfde week; een versieverschil met het overzicht verandert geen getal over een persoon (AD-24).
 */
function NakijkersSeintje({ week, werksoort }: { week: string; werksoort: Werksoort }) {
  const { t } = useTranslation();
  const { getal } = useGetal();
  const signalen = useSignaleringen(week);
  if (signalen.isLoading) return null;

  const antwoord = signalen.data;
  let zin: string;
  if (signalen.isError || antwoord === undefined || (antwoord.data === null && weektoestand(antwoord.reden) !== 'nog_niet_gemeten')) {
    zin = t('qa.team.seintjeNietTeLaden');
  } else if (antwoord.data === null) {
    return null;
  } else {
    // Geen lijst is geen "0 velden": dan is niet te zeggen of nakijkers verschillen.
    const lijst = isObject(antwoord.data) ? antwoord.data.signaleringen : undefined;
    if (!Array.isArray(lijst)) {
      zin = t('qa.team.seintjeNietTeLaden');
    } else if (lijst.length === 0) {
      return null;
    } else {
      zin = t('qa.team.seintje', { count: lijst.length, aantal: getal(lijst.length) });
    }
  }

  return (
    <p
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-[var(--secondary-light)] px-4 py-3 text-[0.75rem] text-[var(--foreground)]"
      data-testid="werkkwaliteit-nakijkers-seintje"
    >
      <span>{zin}</span>
      <Link
        className={`inline-flex items-center gap-1 rounded font-medium text-[var(--secondary-text)] underline-offset-2 hover:underline max-md:min-h-11 [@media(pointer:coarse)]:min-h-11 ${FOCUSRING}`}
        to={`${WERKKWALITEIT_NAKIJKERS}?${new URLSearchParams({ week, werksoort }).toString()}`}
      >
        {t('qa.team.naarNakijkers')}
        <span aria-hidden="true">{TEKEN_NAAR}</span>
      </Link>
    </p>
  );
}

// ── Mini-verloop ────────────────────────────────────────────────────────────────────────────────

/**
 * Het horizontale midden van een staafje, waar het getal erboven staat — geen doel. Als som geschreven omdat de
 * brontoets in `rapportkaart-bron.test.ts` elke losse 50 in deze bron weigert (een vast doel mag nergens staan).
 */
const MIDDEN = `${100 / 2}%`;
const KOLOMMEN = 'grid h-10 grid-cols-4 grid-rows-1 gap-1.5';

/**
 * Vier staafjes, oudste links, in drie lagen met dezelfde kolommen: de staven, de gestippelde doellijn eroverheen
 * (met halo, zichtbaar op een staaf), en de getallen bovenop (met een halo, zodat de doellijn ze niet doorstreept).
 * Elke staaf is een SVG in procentcoördinaten zoals de Doelbalk: elke hele procent valt zonder inline stijl op zijn
 * plek. De `pt-4` erboven houdt het getal bij 100% binnen de tekening. Een week zonder cijfer is
 * `.werkkwaliteit-streep`, zonder getal.
 */
function MiniVerloop({ verloop, doel }: { verloop: Weekcijfer[]; doel?: number }) {
  const { getal } = useGetal();
  const boven = (week: Cijfer) => `${100 - Math.min(100, week.percentage)}%`;
  return (
    <div aria-hidden="true" className="w-[7.5rem] shrink-0 pt-4" data-testid="werkkwaliteit-mini-verloop">
      <div className="relative">
        <div className={KOLOMMEN}>
          {verloop.map((week, plek) =>
            isCijfer(week) ? (
              <svg className="block h-full w-full" height="100%" key={week.weekKey} width="100%">
                <rect
                  className={plek === verloop.length - 1 ? 'fill-[var(--chart-current)]' : 'fill-[var(--chart-past)]'}
                  height={`${Math.min(100, week.percentage)}%`}
                  rx="2"
                  width="100%"
                  x="0"
                  y={boven(week)}
                />
              </svg>
            ) : (
              <div className="werkkwaliteit-streep h-1/4 self-end rounded-[2px]" key={week.weekKey} />
            ),
          )}
        </div>
        {doel !== undefined && (
          <svg className="pointer-events-none absolute inset-0 block h-full w-full overflow-visible" height="100%" overflow="visible" width="100%">
            <line className="stroke-[var(--background)]" strokeWidth="3" x1="0" x2="100%" y1={`${100 - doel}%`} y2={`${100 - doel}%`} />
            <line className="stroke-[var(--chart-goal)]" strokeDasharray="3 2" strokeWidth="1.5" x1="0" x2="100%" y1={`${100 - doel}%`} y2={`${100 - doel}%`} />
          </svg>
        )}
        <div className={`pointer-events-none absolute inset-0 ${KOLOMMEN}`}>
          {verloop.map((week, plek) => (
            // De spatie scheidt de getallen in de tekst (een grid tekent hem niet).
            <Fragment key={week.weekKey}>
              {' '}
              <svg className="block h-full w-full overflow-visible" height="100%" overflow="visible" width="100%">
                {isCijfer(week) && (
                  <text
                    className={`fill-[var(--foreground)] stroke-[var(--background)] text-[0.6875rem] tabular-nums [paint-order:stroke] [stroke-width:3px] ${plek === verloop.length - 1 ? 'font-bold' : ''}`}
                    dy="-0.3em"
                    textAnchor="middle"
                    x={MIDDEN}
                    y={boven(week)}
                  >
                    {getal(week.percentage)}
                  </text>
                )}
              </svg>
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Medewerkerrij ───────────────────────────────────────────────────────────────────────────────

interface RijProps {
  rij: Medewerkersrij;
  werksoort: Werksoort;
  week: string;
  doel?: number;
}

function Badge({ tekst, kleur }: { tekst: string; kleur: 'amber' | 'groen' }) {
  const vlak = kleur === 'amber' ? 'bg-[var(--warning-light)]' : 'bg-[var(--success-light)]';
  const stip = kleur === 'amber' ? 'bg-[var(--warning-text)]' : 'bg-[var(--success-text)]';
  return (
    <span className={`inline-flex min-h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2 text-[0.6875rem] font-medium text-[var(--foreground)] ${vlak}`} data-testid="werkkwaliteit-badge">
      <span aria-hidden="true" className={`size-1.5 shrink-0 rounded-full ${stip}`} />
      {tekst}
    </span>
  );
}

function Medewerkerrij({ rij, werksoort, week, doel }: RijProps) {
  const { t } = useTranslation();
  const { getal } = useGetal();
  const soort = soortVan(rij, API_SLEUTEL[werksoort]);
  const groep = groepVan(soort);
  const naam = rij.naam;
  const naar = `${WERKKWALITEIT_MEDEWERKERS}/${rij.userId}?${new URLSearchParams({ week, werksoort }).toString()}`;
  const link = `grid gap-x-4 gap-y-2 rounded-lg px-4 py-3 hover:bg-[var(--background-muted)] max-md:min-h-11 [@media(pointer:coarse)]:min-h-11 scroll-mt-14 ${FOCUSRING}`;
  const bekijk = (
    <span className="inline-flex items-center gap-1 self-center justify-self-end whitespace-nowrap text-[0.75rem] font-medium text-[var(--secondary-text)]">
      {t('qa.team.rij.bekijk')}
      <span aria-hidden="true">{TEKEN_BEKIJK}</span>
    </span>
  );

  if (groep === 'geen_cijfer' || soort === undefined || !isCijfer(soort.cijfer)) {
    const n = weeknummer(week);
    const cijfer = soort?.cijfer;
    const namen = { werksoort: t(WERKSOORT_LABEL[werksoort]), andere: t(WERKSOORT_LABEL[ANDERE[werksoort]]) };
    let zin = t('qa.team.geenCijfer.geenGegevens', { week: n });
    let aria = zin;
    if (cijfer?.reden === 'te_weinig_gegevens' && isTelling(cijfer.aantalBeoordeeld)) {
      zin = t('qa.team.geenCijfer.teWeinig', { aantal: getal(cijfer.aantalBeoordeeld) });
      aria = t('qa.team.geenCijfer.teWeinigAria', { aantal: getal(cijfer.aantalBeoordeeld) });
    } else if (cijfer?.reden === 'geen_werk_in_werksoort') {
      zin = t('qa.team.geenCijfer.geenWerk', namen);
      aria = t('qa.team.geenCijfer.geenWerkAria', namen);
    } else if (cijfer?.reden === 'niet_nagekeken') {
      zin = t('qa.team.geenCijfer.nietNagekeken', { week: n });
      aria = zin;
    }
    return (
      <li id={`medewerker-${rij.userId}`}>
        <Link aria-label={t('qa.team.rij.linknaamGeenCijfer', { naam, zin: aria, bekijk: t('qa.team.rij.bekijk') })} className={`${link} grid-cols-[minmax(0,1fr)_auto] @xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto]`} to={naar}>
          <span className="min-w-0 text-[0.8125rem] font-medium text-[var(--foreground-muted)] [overflow-wrap:anywhere]">{naam}</span>
          <span className="col-start-1 min-w-0 text-[0.75rem] text-[var(--foreground-muted)] @xl:col-start-2 @xl:row-start-1">{zin}</span>
          <span className="col-start-2 row-span-2 row-start-1 self-center justify-self-end @xl:col-start-3 @xl:row-span-1">{bekijk}</span>
        </Link>
      </li>
    );
  }

  const cijfer = soort.cijfer;
  const verloop = geldigVerloop(soort);
  const beschrijvingId = `medewerker-${rij.userId}-verloop`;
  const badges =
    groep === 'aandacht'
      ? [{ tekst: t('qa.team.badge.onderDoel'), kleur: 'amber' as const }, { tekst: t('qa.team.badge.dalend'), kleur: 'amber' as const }]
      : soort.onderDoel === true
        ? [{ tekst: t('qa.team.badge.onderDoel'), kleur: 'amber' as const }]
        : [{ tekst: t('qa.team.badge.opKoers'), kleur: 'groen' as const }];

  const vorigeWeek = weeknummer(soort.vorigeWeek?.weekKey ?? '');
  const richting =
    (soort.richting === 'hoger' || soort.richting === 'lager') && isTelling(soort.verschilPunten)
      ? t(soort.richting === 'hoger' ? 'qa.team.rij.richtingHoger' : 'qa.team.rij.richtingLager', {
          count: soort.verschilPunten,
          punten: getal(soort.verschilPunten),
          week: vorigeWeek,
        })
      : soort.richting === 'gelijk'
        ? t('qa.team.rij.richtingGelijk', { week: vorigeWeek })
        : t('qa.team.rij.richtingGeenVergelijking');
  const scheiding = t('qa.team.scheiding');
  const linknaam = t('qa.team.rij.linknaamCijfer', {
    naam,
    percentage: getal(cijfer.percentage),
    goed: getal(cijfer.aantalInEenKeerGoed),
    beoordeeld: getal(cijfer.aantalBeoordeeld),
    richting,
    badges: badges.map((badge) => badge.tekst).join(scheiding),
    // WCAG 2.5.3: de naam bevat het zichtbare "Bekijk", zodat spraakbediening de link vindt.
    bekijk: t('qa.team.rij.bekijk'),
  });
  const beschrijving =
    verloop === null
      ? null
      : t('qa.team.verloop.beschrijving', {
          weken: [
            ...verloop.map((w) =>
              isCijfer(w)
                ? t('qa.team.verloop.week', { week: weeknummer(w.weekKey), percentage: getal(w.percentage) })
                : t('qa.team.verloop.weekGeenCijfer', { week: weeknummer(w.weekKey) }),
            ),
            ...(doel === undefined ? [] : [t('qa.team.verloop.doel', { doel: getal(doel) })]),
          ].join(scheiding),
        });

  return (
    <li id={`medewerker-${rij.userId}`}>
      <Link
        aria-describedby={beschrijvingId}
        aria-label={linknaam}
        className={`${link} grid-cols-[minmax(0,1fr)_auto] @xl:grid-cols-[minmax(0,1fr)_minmax(4.5rem,auto)_auto_minmax(0,1fr)_auto] @xl:items-center`}
        to={naar}
      >
        <span className="min-w-0 self-center text-[0.8125rem] font-medium text-[var(--foreground)] [overflow-wrap:anywhere]">{naam}</span>
        <span className="flex flex-col items-end @xl:items-start">
          <span className="text-[1.25rem] font-semibold leading-none tabular-nums text-[var(--foreground-heading)]">
            {t('qa.rapportkaart.percentage', { percentage: getal(cijfer.percentage) })}
          </span>
          <span className="text-[0.75rem] tabular-nums text-[var(--foreground-muted)]">
            {t('qa.team.rij.xVanY', { goed: getal(cijfer.aantalInEenKeerGoed), beoordeeld: getal(cijfer.aantalBeoordeeld) })}
          </span>
        </span>
        <span className="col-span-2 @xl:col-span-1">
          {verloop === null ? (
            <span className="text-[0.75rem] text-[var(--foreground-muted)]" id={beschrijvingId}>
              {t('qa.team.verloop.nietTeLaden')}
            </span>
          ) : (
            <>
              <MiniVerloop doel={doel} verloop={verloop} />
              <span className="sr-only" id={beschrijvingId}>
                {beschrijving}
              </span>
            </>
          )}
        </span>
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <Richtingstekst richting={soort.richting} verschilPunten={soort.verschilPunten} />
          {badges.map((badge) => (
            <Badge key={badge.tekst} kleur={badge.kleur} tekst={badge.tekst} />
          ))}
        </span>
        {bekijk}
      </Link>
    </li>
  );
}

// ── Team ────────────────────────────────────────────────────────────────────────────────────────

/** Alfabetisch op voornaam (zonder voornaam: de naam), dan achternaam; gelijk blijft in de volgorde van het antwoord (M8). */
function alfabetisch(rijen: Medewerkersrij[], locale: string): Medewerkersrij[] {
  const eerste = (rij: Medewerkersrij) => rij.voornaam ?? rij.naam;
  return [...rijen].sort((a, b) => eerste(a).localeCompare(eerste(b), locale) || (a.achternaam ?? '').localeCompare(b.achternaam ?? '', locale));
}

export interface TeamOverzichtProps {
  overzicht: Medewerkersoverzicht;
  rijen: Medewerkersrij[];
  week: string;
  werksoort: Werksoort;
}

/**
 * De gewone weergave van Team (S1, variant A, CAP-1): Teamzin met Doelbalk, het Nakijkers-seintje, drie groepen
 * met hun rijen, de Verlooplegenda en de Zelf-afgetekend-regel. Elk getal komt klaar uit het antwoord (AD-19);
 * hier wordt alleen alfabetisch gesorteerd. De groep is die van de dienst.
 */
export function TeamOverzicht({ overzicht, rijen, week, werksoort }: TeamOverzichtProps) {
  const { t } = useTranslation();
  const { locale, getal } = useGetal();
  const sleutel = API_SLEUTEL[werksoort];
  const naam = t(WERKSOORT_LABEL[werksoort]);
  const team = overzicht.team?.[sleutel];
  const doel = isTelling(overzicht.afbakening.doelInEenKeerGoed) ? overzicht.afbakening.doelInEenKeerGoed : undefined;
  const perGroep = isObject(team) && isObject(team.aantalPerGroep) ? team.aantalPerGroep : null;

  const gesorteerd = alfabetisch(rijen, locale);
  const groepen = GROEPEN.map((groep) => ({ groep, rijen: gesorteerd.filter((rij) => groepVan(soortVan(rij, sleutel)) === groep) })).filter(
    ({ rijen: inGroep }) => inGroep.length > 0,
  );

  const legendaVerloop = gesorteerd
    .map((rij) => soortVan(rij, sleutel))
    .map((soort) => (soort !== undefined && isCijfer(soort.cijfer) ? geldigVerloop(soort) : null))
    .find((verloop) => verloop !== null);

  const zelf = overzicht.zelfAfgetekendPerWerksoort?.[sleutel];
  const zelfPercentage =
    isObject(zelf) && zelf.reden === undefined && isTelling(zelf.aantal) && zelf.aantal > 0 && isTelling(zelf.percentage) && zelf.percentage > 0
      ? zelf.percentage
      : null;

  return (
    <>
      {isObject(team) && <Teamzin doel={doel} naam={naam} team={team} week={week} />}
      <NakijkersSeintje week={week} werksoort={werksoort} />
      {groepen.map(({ groep, rijen: inGroep }) => {
        const aantal = perGroep !== null && isTelling(perGroep[groep]) ? perGroep[groep] : inGroep.length;
        return (
          <div
            className="flex min-w-0 flex-col rounded-xl bg-[var(--background)] shadow-[var(--card-shadow)]"
            data-testid={`werkkwaliteit-team-groep-${groep}`}
            key={groep}
          >
            <div className="flex flex-col gap-0.5 px-4 pb-2 pt-4">
              <h2 className="text-[0.75rem] font-semibold text-[var(--foreground-heading)]">{t(`qa.team.groep.${groep}`, { count: aantal, aantal: getal(aantal) })}</h2>
              <p className="text-[0.75rem] text-[var(--foreground-muted)]">
                {groep === 'aandacht' && doel === undefined
                  ? t('qa.team.uitleg.aandachtZonderDoel')
                  : t(`qa.team.uitleg.${groep}`, doel === undefined ? {} : { doel: getal(doel) })}
              </p>
            </div>
            <ul className="@container flex flex-col divide-y divide-[var(--border-soft)] px-1 pb-1">
              {inGroep.map((rij) => (
                <Medewerkerrij doel={doel} key={rij.userId} rij={rij} week={week} werksoort={werksoort} />
              ))}
            </ul>
          </div>
        );
      })}
      {legendaVerloop !== undefined && legendaVerloop !== null && (
        <ul
          aria-label={t('qa.team.legenda.label')}
          className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[0.6875rem] text-[var(--foreground-muted)]"
          data-testid="werkkwaliteit-verlooplegenda"
        >
          <li className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="inline-block h-3 w-2 rounded-[2px] bg-[var(--chart-current)]" />
            {t('qa.team.legenda.rechts', { week: weeknummer(legendaVerloop[3]!.weekKey) })}
          </li>
          <li className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="inline-block h-3 w-2 rounded-[2px] bg-[var(--chart-past)]" />
            {t('qa.team.legenda.eerder', { van: weeknummer(legendaVerloop[0]!.weekKey), tot: weeknummer(legendaVerloop[2]!.weekKey) })}
          </li>
          <li className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="werkkwaliteit-streep inline-block h-3 w-2 rounded-[2px]" />
            {t('qa.team.legenda.gestreept')}
          </li>
          {doel !== undefined && (
            <li className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className="inline-block w-4 border-t-2 border-dashed border-[var(--chart-goal)]" />
              {t('qa.team.legenda.doel', { doel: getal(doel) })}
            </li>
          )}
        </ul>
      )}
      {zelfPercentage !== null && (
        <p className="text-[0.75rem] text-[var(--foreground-muted)]" data-testid="werkkwaliteit-zelf-afgetekend">
          {t('qa.team.zelfAfgetekend', { percentage: getal(zelfPercentage) })}
        </p>
      )}
    </>
  );
}
